import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, CarFront, Server, Radar, Code2, Lock } from "lucide-react";
import { useLanguage } from "@/i18n";
import Reveal, { MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CyberCanvas from "@/components/CyberCanvas";

const IT_IMG = "https://images.unsplash.com/photo-1767817099805-d79e31fb968c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxjeWJlcnNlY3VyaXR5JTIwY29kZSUyMGRhcmslMjBzY3JlZW4lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc5MzYyOTQ5MXww&ixlib=rb-4.1.0&q=85";
const TRANSPORT_IMG = "https://images.unsplash.com/photo-1681167816895-940c56e0d2a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjaGF1ZmZldXIlMjBkcml2ZXIlMjBleGVjdXRpdmUlMjBjYXIlMjB2ZWhpY2xlJTIwcmVudGFsfGVufDB8fHx8MTc5MzYyOTQ5MXww&ixlib=rb-4.1.0&q=85";
const ALPINE_IMG = "https://images.unsplash.com/photo-1586376087814-7080e39b82da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxTd2lzcyUyMGFscHMlMjBsdXh1cnklMjBleGVjdXRpdmUlMjBzZWRhbiUyMGNhcnxlbnwwfHx8fDE3OTM2Mjk0OTd8MA&ixlib=rb-4.1.0&q=85";

const SplitGateway = () => {
  const { t } = useLanguage();
  const [hovered, setHovered] = useState(null);

  return (
    <section
      data-testid="split-gateway"
      className="relative flex min-h-[100svh] flex-col md:flex-row"
      onMouseLeave={() => setHovered(null)}
    >
      <div className="pointer-events-none absolute inset-x-0 top-16 z-30 flex justify-center">
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="rounded-full border border-white/15 bg-black/50 px-5 py-2 backdrop-blur-md"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-slate-300 sm:text-xs">{t.home.eyebrow}</span>
        </motion.div>
      </div>

      <motion.div
        data-testid="split-pole-it"
        onMouseEnter={() => setHovered("it")}
        animate={{ flexGrow: hovered === "it" ? 1.6 : hovered === "transport" ? 0.72 : 1 }}
        transition={{ type: "spring", stiffness: 90, damping: 20 }}
        className="relative flex min-h-[50svh] flex-1 basis-0 flex-col justify-end overflow-hidden md:min-h-[100svh]"
      >
        <div className="absolute inset-0 bg-[#0A0D14]" />
        <img src={IT_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/70 to-[#0A0D14]/30" />
        <div className="absolute inset-0 bg-grid-dark" />
        <CyberCanvas className="hidden md:block" />
        <div className="relative z-10 p-6 pb-10 sm:p-10 md:p-14">
          <MaskedLines
            baseDelay={0.15}
            lines={[t.home.it.label]}
            lineClassName="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-cyan-400"
          />
          <MaskedLines
            baseDelay={0.3}
            lines={t.home.it.title.split(" & ")}
            className="mt-4"
            lineClassName="text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-5 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base"
          >
            {t.home.it.desc}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.6 }}
            className="mt-7 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/it"
              data-testid="split-pole-it-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-950 transition-colors duration-200 hover:bg-cyan-300"
            >
              {t.home.it.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <div className="flex flex-wrap gap-2">
              {t.home.it.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-cyan-400/25 bg-cyan-400/5 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-300/90">
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
        <ShieldCheck className="absolute right-8 top-24 hidden h-20 w-20 text-cyan-400/15 md:block" strokeWidth={1} />
      </motion.div>

      <div className="relative z-20 flex items-center justify-center md:absolute md:inset-y-0 md:left-1/2 md:-translate-x-1/2">
        <div className="animate-beam h-1 w-full bg-gradient-to-r from-cyan-400 via-white to-red-500 md:h-full md:w-1 md:bg-gradient-to-b" />
      </div>

      <motion.div
        data-testid="split-pole-transport"
        onMouseEnter={() => setHovered("transport")}
        animate={{ flexGrow: hovered === "transport" ? 1.6 : hovered === "it" ? 0.72 : 1 }}
        transition={{ type: "spring", stiffness: 90, damping: 20 }}
        className="relative flex min-h-[50svh] flex-1 basis-0 flex-col justify-end overflow-hidden md:min-h-[100svh]"
      >
        <div className="absolute inset-0 bg-[#F8FAFC]" />
        <img src={TRANSPORT_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F8FAFC] via-[#F8FAFC]/70 to-[#F8FAFC]/20" />
        <div className="absolute inset-0 bg-grid-light" />
        <div className="relative z-10 p-6 pb-10 sm:p-10 md:p-14 md:text-right md:[&>div]:items-end">
          <MaskedLines
            baseDelay={0.2}
            lines={[t.home.transport.label]}
            lineClassName="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-red-600"
          />
          <MaskedLines
            baseDelay={0.35}
            lines={t.home.transport.title.split(" & ")}
            className="mt-4"
            lineClassName="text-3xl font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-5 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base md:ml-auto"
          >
            {t.home.transport.desc}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-7 flex flex-wrap items-center gap-3 md:justify-end"
          >
            <Link
              to="/transport"
              data-testid="split-pole-transport-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-600"
            >
              {t.home.transport.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <div className="flex flex-wrap gap-2">
              {t.home.transport.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-slate-300 bg-white/60 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
        <CarFront className="absolute right-8 top-24 hidden h-20 w-20 text-slate-900/10 md:block" strokeWidth={1} />
      </motion.div>
    </section>
  );
};

const Home = () => {
  const { t } = useLanguage();

  return (
    <main data-testid="home-page" className="bg-[#070A10]">
      <SplitGateway />
      <Marquee items={t.home.marquee} />

      <section data-testid="home-about-section" className="relative overflow-hidden bg-[#070A10] px-4 py-24 sm:px-6 sm:py-32">
        <div className="absolute inset-0 bg-grid-dark opacity-60" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">{t.home.aboutEyebrow}</p>
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t.home.aboutTitle}
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">{t.home.aboutText}</p>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {t.home.stats.map((s) => (
                <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="text-2xl font-extrabold text-cyan-300 sm:text-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.v}</p>
                  <p className="mt-1 text-xs leading-snug text-slate-500">{s.l}</p>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="home-about-cta-btn"
              className="mt-10 inline-flex items-center gap-2 rounded-full border border-cyan-400/40 px-6 py-3 text-sm font-bold text-cyan-300 transition-colors duration-200 hover:bg-cyan-400 hover:text-slate-950"
            >
              {t.home.aboutCta}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl border border-white/10">
                <img src={ALPINE_IMG} alt="Route alpine suisse" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-6 -left-4 flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0A0D14]/90 p-5 backdrop-blur-md sm:-left-8">
                <div className="flex gap-3 text-cyan-400">
                  <Code2 className="h-6 w-6" strokeWidth={1.5} />
                  <Lock className="h-6 w-6" strokeWidth={1.5} />
                  <Radar className="h-6 w-6" strokeWidth={1.5} />
                  <Server className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-slate-400">
                  Swiss precision
                  <br />
                  since day one
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
